import {useEffect,useRef,useState} from 'react';
import {DEFAULT_HELM_HEADER} from '../assets/helmHeader';
import {extraLocationSettingSections} from '../../worker/domain/location-settings-fields';

type Branding={showLoginMasthead:boolean;hasCustomImage:boolean};
type Logos={hasLightLogo:boolean;hasDarkLogo:boolean;useShopLogoOnPublicPages:boolean};
type Section=string;
type InputKind='text'|'textarea'|'boolean'|'url'|'number'|'email'|'time'|'select';
type Field={key:string;label:string;kind:InputKind;options?:readonly string[]};
type Config=Record<Section,Record<string,unknown>>;
const legacySections:{key:Section;title:string;fields:Field[]}[]=[
  {key:'publicMessages',title:'Public Booking and Store Messages',fields:[
    {key:'bookingHelpMessage',label:'Booking Help Message (optional)',kind:'textarea'},
    {key:'bookingDailyAlertMessage',label:'Booking Daily Alert Message (optional)',kind:'textarea'},
    {key:'bookingMenuHelpMessage',label:'Help Message below Booking Menu (optional)',kind:'textarea'},
    {key:'todaysFullMessage',label:"Today's Full Message (optional)",kind:'textarea'},
    {key:'storeHelpMessage',label:'Store Help Message (optional)',kind:'text'},
    {key:'servicesSectionMessage',label:'Services Section Message (optional)',kind:'textarea'},
    {key:'packagesSectionMessage',label:'Packages Section Message (optional)',kind:'textarea'},
    {key:'membershipsSectionMessage',label:'Memberships Section Message (optional)',kind:'textarea'},
    {key:'giftCardStoreHelpMessage',label:'Gift Card Store Help Message (optional)',kind:'textarea'},
  ]},
  {key:'redirects',title:'Post Booking and Purchase Redirects',fields:[
    {key:'postBookingUrl',label:'Post-Booking URL (optional)',kind:'url'},
    {key:'postPurchaseUrl',label:'Post-Purchase URL (optional)',kind:'url'},
  ]},
  {key:'analytics',title:'Third Party Analytics',fields:[
    {key:'googleAnalyticsCode',label:'Google Analytics Code (optional)',kind:'text'},
    {key:'googleAdsConversionId',label:'Google Ads Conversion ID (optional)',kind:'text'},
    {key:'googleAdsConversionLabel',label:'Google Ads Conversion Label (optional)',kind:'text'},
    {key:'googleTagManagerCode',label:'Google Tag Manager Code (optional)',kind:'text'},
    {key:'metaTrackingPixel',label:'Facebook/Meta Tracking Pixel (optional)',kind:'text'},
    {key:'facebookConversionToken',label:'Facebook Conversion Token (optional)',kind:'text'},
    {key:'customHtmlHeadContent',label:'Custom HTML HEAD Content (optional)',kind:'textarea'},
    {key:'enableCustomHtmlHeadContent',label:'Enable Custom HTML HEAD Content',kind:'boolean'},
  ]},
  {key:'features',title:'Individual Feature Settings',fields:[
    {key:'enableEmailing',label:'Auto-Emails: Enable Emailing',kind:'boolean'},
    {key:'allowCheckIn',label:'Enable Guest Check-In on Staff Schedule',kind:'boolean'},
    {key:'allowMinorWaiver',label:'Waivers: Allow customers to sign waivers on behalf of a minor',kind:'boolean'},
    {key:'defaultGiftCardSeries',label:'Gift Cards: Default Gift Card Series',kind:'text'},
    {key:'giftCardPrefix',label:'Location Gift Card Codes: Prefix (optional)',kind:'text'},
    {key:'giftCardSuffix',label:'Location Gift Card Codes: Suffix (optional)',kind:'text'},
    {key:'giftCardsOnlyExpireFreeCredit',label:"Gift cards only expire 'free' credit",kind:'boolean'},
    {key:'giftCardsUseFreeCreditFirst',label:"Gift cards use 'free' credit first",kind:'boolean'},
    {key:'onlineGiftCardPopupThreshold',label:'Online Gift Card Store: Popup threshold',kind:'number'},
    {key:'customersCanPauseMemberships',label:'Customers can pause their own memberships online',kind:'boolean'},
    {key:'membershipPauseHelpMessage',label:'Membership Pause Help Message (optional)',kind:'textarea'},
    {key:'autoGenerateDescriptions',label:'Auto-Generate Missing Package and Membership Descriptions',kind:'boolean'},
    {key:'defaultCustomerTaskProject',label:'Default Project for Tasks linked to Customers',kind:'text'},
  ]},
];
const sections:{key:Section;title:string;fields:readonly Field[];description?:string}[]=[
  ...legacySections,...extraLocationSettingSections];
const emptyConfig=():Config=>Object.fromEntries(sections.map(s=>[s.key,{}]));


type PublicStoreSortable={id:string;name:string;kind:'service'|'package';
  featured:boolean;recommendedAddOn:boolean};
function PublicStoreOrderEditor({locationCode}:{locationCode:string}){
  const [services,setServices]=useState<PublicStoreSortable[]>([]);
  const [packages,setPackages]=useState<PublicStoreSortable[]>([]);
  const [message,setMessage]=useState('');
  const [busy,setBusy]=useState(false);
  useEffect(()=>{
    const controller=new AbortController();
    setMessage('');
    void fetch('/api/v1/shop/catalog?'+new URLSearchParams({location:locationCode}),{
      cache:'no-store',credentials:'same-origin',signal:controller.signal})
      .then(async response=>{
        if(!response.ok)throw Error('Store order unavailable.');
        const body=await response.json() as {ok:boolean;data?:{
          services:PublicStoreSortable[];packages:PublicStoreSortable[]}};
        if(!body.ok||!body.data)throw Error('Store order unavailable.');
        if(!controller.signal.aborted){
          setServices(body.data.services);setPackages(body.data.packages);
        }
      }).catch(()=>{if(!controller.signal.aborted)setMessage('Unable to load Store order.');});
    return ()=>controller.abort();
  },[locationCode]);
  const move=(kind:'service'|'package',index:number,step:number)=>{
    const setter=kind==='service'?setServices:setPackages;
    setter(rows=>{
      const next=[...rows],to=index+step;
      if(to<0||to>=next.length)return rows;
      [next[index],next[to]]=[next[to],next[index]];
      return next;
    });setMessage('');
  };
  const save=async(kind:'service'|'package')=>{
    setBusy(true);setMessage('Saving…');
    try{
      const ids=(kind==='service'?services:packages).map(item=>item.id);
      const response=await fetch('/api/v1/staff/shop/order',{
        method:'PATCH',cache:'no-store',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,kind,ids}),
      });
      if(!response.ok)throw Error('Could not save Store order.');
      setMessage('Store order saved.');
    }catch(e){setMessage(e instanceof Error?e.message:'Unable to save.');}
    finally{setBusy(false);}
  };
  const updateMerchandising=async(item:PublicStoreSortable,
    field:'featured'|'recommendedAddOn',checked:boolean)=>{
    const next={...item,[field]:checked};
    const setter=item.kind==='service'?setServices:setPackages;
    // Make the controlled checkbox reflect the requested change immediately.
    // Restore its previous value if the authenticated server write fails.
    setter(rows=>rows.map(row=>row.id===item.id?next:row));
    setBusy(true);setMessage('Saving…');
    try{
      const response=await fetch('/api/v1/staff/shop/merchandising',{
        method:'PATCH',cache:'no-store',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,kind:item.kind,id:item.id,
          featured:next.featured,recommendedAddOn:next.recommendedAddOn}),
      });
      if(!response.ok)throw Error(response.status===409?
        'This item is no longer offered in the Store. Reload the catalog.':
        'Store merchandising could not be saved.');
      setMessage('Store merchandising saved.');
    }catch(e){
      setter(rows=>rows.map(row=>row.id===item.id?item:row));
      setMessage(e instanceof Error?e.message:'Unable to save.');
    }
    finally{setBusy(false);}
  };
  return <details className="ecfs-shop-setting-panel">
    <summary>Public Store Product Order and Featured Items</summary>
    <div className="ecfs-location-fields">
      <p>Arrange the products shown in the Store at this location.
        Featured products show a Most Popular label. Select optional items to suggest before checkout.</p>
      {([{key:'package',label:'Packages',items:packages},
        {key:'service',label:'Services',items:services}] as const).map(group=>
        <div key={group.key}>
          <h3>{group.label}</h3>
          {group.items.map((item,index)=><div key={item.id}
            className="ecfs-public-store-order-row">
            <span className="ecfs-public-store-order-name">{item.name}</span>
            <label><input type="checkbox" disabled={busy}
              checked={item.featured}
              onChange={event=>void updateMerchandising(item,'featured',event.target.checked)}/>
              Most Popular</label>
            <label><input type="checkbox" disabled={busy}
              checked={item.recommendedAddOn}
              onChange={event=>void updateMerchandising(item,'recommendedAddOn',
                event.target.checked)}/>
              Suggest at Checkout</label>
            <button type="button" aria-label={'Move '+item.name+' up'}
              disabled={busy||index===0}
              onClick={()=>move(group.key,index,-1)}>↑</button>
            <button type="button" aria-label={'Move '+item.name+' down'}
              disabled={busy||index===group.items.length-1}
              onClick={()=>move(group.key,index,1)}>↓</button>
          </div>)}
          <button type="button" disabled={busy||!group.items.length}
            onClick={()=>void save(group.key)}>Save {group.label} Order</button>
        </div>)}
      {message&&<p role="status">{message}</p>}
    </div>
  </details>;
}

/** Exact legacy location selection determines the record edited. Settings are
 * persisted separately for each location, not shared globally. Feature values
 * are configuration only until their associated runtime module is implemented.
 */
export function ShopSettingsWorkspace({locationCode}:{locationCode:string}){
  const [branding,setBranding]=useState<Branding|null>(null);
  const [logos,setLogos]=useState<Logos|null>(null);
  const [logoRevision,setLogoRevision]=useState(0);
  const [config,setConfig]=useState<Config>(emptyConfig);
  const [revision,setRevision]=useState(0);
  const [version,setVersion]=useState(0);
  const [loading,setLoading]=useState(true);
  const [status,setStatus]=useState('');
  const [sectionStatus,setSectionStatus]=useState<Partial<Record<Section,string>>>({});
  const picker=useRef<HTMLInputElement|null>(null);
  useEffect(()=>{
    const controller=new AbortController();
    setLoading(true);setStatus('');setBranding(null);setLogos(null);setConfig(emptyConfig());
    const p=new URLSearchParams({location:locationCode});
    void Promise.all([
      fetch('/api/v1/shop/branding?'+p,{cache:'no-store',signal:controller.signal}),
      fetch('/api/v1/staff/location-settings?'+p,{
        credentials:'same-origin',cache:'no-store',signal:controller.signal}),
      fetch('/api/v1/shop/logos?'+p,{cache:'no-store',signal:controller.signal}),
    ]).then(async([a,b,c])=>{
      if(!a.ok||!b.ok||!c.ok)throw Error(b.status===403?'Access denied.':'Location settings unavailable.');
      const brandingResult=await a.json() as {ok:boolean;data?:Branding};
      const logoResult=await c.json() as {ok:boolean;data?:Logos};
      const settingsResult=await b.json() as {ok:boolean;data?:{
        version:number;config:Partial<Config>}};
      if(!brandingResult.ok||!brandingResult.data||!logoResult.ok||!logoResult.data||
        !settingsResult.ok||!settingsResult.data)throw Error('Location settings unavailable.');
      if(controller.signal.aborted)return;
      setBranding(brandingResult.data);setLogos(logoResult.data);
      setVersion(settingsResult.data.version);
      const received=settingsResult.data.config;
      setConfig(Object.fromEntries(sections.map(section=>
        [section.key,received[section.key]??{}])));
    }).catch(error=>{if(!controller.signal.aborted)setStatus(
      error instanceof Error?error.message:'Location settings unavailable.');
    }).finally(()=>{if(!controller.signal.aborted)setLoading(false);});
    return ()=>controller.abort();
  },[locationCode]);
  const changeValue=(section:Section,key:string,value:unknown)=>{
    setConfig(previous=>({...previous,
      [section]:{...previous[section],[key]:value}}));
    setSectionStatus(previous=>({...previous,[section]:''}));
  };
  const saveSection=async(section:Section)=>{
    setSectionStatus(previous=>({...previous,[section]:'Saving…'}));
    try{
      const fields=sections.find(item=>item.key===section)?.fields??[];
      const values:Record<string,unknown>={};
      for(const field of fields){
        const value=config[section][field.key];
        if(field.kind==='number'){
          if(value!==undefined&&value!==''&&value!==null)values[field.key]=Number(value);
        }else if(field.kind==='boolean')values[field.key]=value===true;
        else values[field.key]=typeof value==='string'?value:'';
      }
      const response=await fetch('/api/v1/staff/location-settings',{
        method:'PATCH',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,section,expectedVersion:version,values}),
      });
      const result=await response.json() as {ok:boolean;data?:{
        version:number;config:Partial<Config>};error?:{code?:string}};
      if(!response.ok||!result.ok||!result.data)
        throw Error(response.status===409?
          'Settings changed elsewhere. Reload before saving.':
          'Location settings could not be saved.');
      setVersion(result.data.version);
      setSectionStatus(previous=>({...previous,[section]:'Saved'}));
    }catch(e){setSectionStatus(previous=>({...previous,[section]:
      e instanceof Error?e.message:'Save failed.'}));}
  };
  const changeVisible=async(enabled:boolean)=>{
    setStatus('Saving…');
    try{
      const response=await fetch('/api/v1/staff/shop/branding',{
        method:'PATCH',credentials:'same-origin',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,showLoginMasthead:enabled}),
      });
      if(!response.ok)throw Error('Location header could not be saved.');
      setBranding(previous=>previous?{...previous,showLoginMasthead:enabled}:previous);
      setStatus('Saved');
    }catch(e){setStatus(e instanceof Error?e.message:'Save failed.');}
  };
  const upload=async(file:File|undefined)=>{
    if(!file)return;
    if(!['image/png','image/jpeg','image/webp'].includes(file.type)||file.size>1048576){
      setStatus('Choose a JPG, PNG or WebP under 1 MB.');return;
    }
    setStatus('Uploading header image…');
    try{
      const response=await fetch('/api/v1/staff/shop/masthead?'+
        new URLSearchParams({location:locationCode}),{
          method:'PUT',credentials:'same-origin',headers:{'Content-Type':file.type},body:file});
      if(!response.ok)throw Error('Location header could not be saved.');
      setRevision(x=>x+1);
      setBranding(previous=>previous?{...previous,hasCustomImage:true}:previous);
      setStatus('Header saved.');
    }catch(e){setStatus(e instanceof Error?e.message:'Header upload failed.');}
  };

  const uploadLogo=async(file:File|undefined,mode:'light'|'dark')=>{
    if(!file)return;
    if(!['image/png','image/jpeg','image/webp'].includes(file.type)||file.size>1048576){
      setStatus('Choose a JPG, PNG or WebP under 1 MB.');return;
    }
    setStatus('Uploading location logo…');
    try{
      const response=await fetch('/api/v1/staff/shop/logo?'+
        new URLSearchParams({location:locationCode,mode}),{
          method:'PUT',credentials:'same-origin',headers:{'Content-Type':file.type},
          body:file,
        });
      if(!response.ok)throw Error('Location logo could not be saved.');
      setLogos(previous=>previous?{...previous,
        [mode==='dark'?'hasDarkLogo':'hasLightLogo']:true}:previous);
      setLogoRevision(x=>x+1);setStatus('Logo saved.');
    }catch(e){setStatus(e instanceof Error?e.message:'Logo upload failed.');}
  };
  const updateLogoPublic=async(enabled:boolean)=>{
    setStatus('Saving…');
    try{
      const response=await fetch('/api/v1/staff/shop/logo-settings',{
        method:'PATCH',credentials:'same-origin',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,useShopLogoOnPublicPages:enabled}),
      });
      if(!response.ok)throw Error('Location logo setting could not be saved.');
      setLogos(previous=>previous?{...previous,useShopLogoOnPublicPages:enabled}:previous);
      setStatus('Saved');
    }catch(e){setStatus(e instanceof Error?e.message:'Save failed.');}
  };
  const src=branding?.hasCustomImage?
    '/api/v1/shop/masthead?'+new URLSearchParams({
      location:locationCode,v:String(revision)}):DEFAULT_HELM_HEADER;
  return <section className="ecfs-shop-settings" aria-label="Location Settings">
    <h1>Location Settings</h1>
    {loading&&<p role="status">Loading location settings…</p>}
    {status&&<p role="status">{status}</p>}
    {!loading&&branding&&<div className="ecfs-location-accordion">
      {sections.map((section,index)=><details key={section.key} open={index===0?true:undefined}
        className="ecfs-shop-setting-panel">
        <summary>{section.title}</summary>
        <div className="ecfs-location-fields">
          {section.description&&<p>{section.description}</p>}
          {section.fields.map(field=><label key={field.key}
            className={'ecfs-location-field ecfs-location-field-'+field.kind}>
            {field.kind==='boolean'
              ?<><input type="checkbox"
                  checked={config[section.key][field.key]===true}
                  onChange={event=>changeValue(section.key,field.key,event.target.checked)}/>
                {field.label}</>
              :<><span>{field.label}</span>
                {field.kind==='textarea'
                  ?<textarea rows={3} value={String(config[section.key][field.key]??'')}
                    onChange={event=>changeValue(section.key,field.key,event.target.value)}/>
                  :field.kind==='select'
                  ?<select value={String(config[section.key][field.key]??'')}
                    onChange={event=>changeValue(section.key,field.key,event.target.value)}>
                    <option value="">Select…</option>
                    {(field.options??[]).map(option=>
                      <option value={option} key={option}>{option}</option>)}
                  </select>
                  :<input type={field.kind==='url'?'url':
                      field.kind==='number'?'number':
                      field.kind==='email'?'email':
                      field.kind==='time'?'time':'text'}
                    value={String(config[section.key][field.key]??'')}
                    step={field.kind==='number'?'any':undefined}
                    onChange={event=>changeValue(section.key,field.key,event.target.value)}/>}</>}
          </label>)}
          <div className="ecfs-location-save">
            <button type="button" onClick={()=>void saveSection(section.key)}>Save</button>
            {sectionStatus[section.key]&&<span role="status">{sectionStatus[section.key]}</span>}
          </div>
        </div>
      </details>)}
      <PublicStoreOrderEditor locationCode={locationCode}/>
      <details className="ecfs-shop-setting-panel">
        <summary>Location Logo</summary>
        <div className="ecfs-location-fields">
          {(['light','dark'] as const).map(mode=><label key={mode}
            className="ecfs-location-field">
            <span>{mode==='light'?'Shop Logo':'Dark Mode Logo (optional)'}</span>
            {(mode==='light'?logos?.hasLightLogo:logos?.hasDarkLogo)&&
              <img className="ecfs-location-logo-preview"
                alt={mode==='light'?'Location logo':'Dark mode location logo'}
                src={'/api/v1/shop/logo?'+new URLSearchParams({
                  location:locationCode,mode,v:String(logoRevision)})}/>}
            <input type="file" accept="image/png,image/jpeg,image/webp"
              aria-label={mode==='light'?'Choose shop logo':'Choose dark mode logo'}
              onChange={event=>{const file=event.target.files?.[0];event.target.value='';
                void uploadLogo(file,mode);}}/>
          </label>)}
          <label className="ecfs-location-field-boolean"><input type="checkbox"
            checked={logos?.useShopLogoOnPublicPages??false}
            onChange={event=>void updateLogoPublic(event.target.checked)}/>
            Use Shop Logo on Public Pages
          </label>
        </div>
      </details>
      <details className="ecfs-shop-setting-panel">
        <summary>Login Page Header Image</summary>
        <div className="ecfs-location-fields">
          <img src={src} alt="Configured spa login header" className="ecfs-shop-header-preview"/>
          <label><input type="checkbox" checked={branding.showLoginMasthead}
            onChange={event=>void changeVisible(event.target.checked)}/>
            Show header image on login page
          </label>
          <button type="button" onClick={()=>picker.current?.click()}>
            Upload / Replace Header Image
          </button>
          <input ref={picker} className="ecfs-avatar-file" type="file"
            accept="image/png,image/jpeg,image/webp" aria-label="Choose login header image"
            onChange={event=>{const file=event.target.files?.[0];event.target.value='';
              void upload(file);}}/>
        </div>
      </details>
    </div>}
  </section>;
}
